(function () {
  const defaultMoneyFormat = '${{amount}}';
  const cartRateCacheKey = 'nianave:currency-rate-cache';

  /**
   * Format money values based on the shop locale and currency settings
   * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents or 3.00 dollars
   * @param  {String} format - optional custom format string (fallback for unsupported locales)
   * @return {String} value - formatted value
   */
  var formatMoney = function(cents, format) {
      if (typeof cents === 'string') {
          cents = cents.replace('.', '');
      }

      // Same output as Liquid's money filter: the store's own format string
      // (e.g. "{{amount_with_comma_separator}} KM" -> "115,00 KM"), spacing kept.
      const formatString = format || window.moneyFormat || defaultMoneyFormat;
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const amount = Number(cents) / 100;

      const formatWithDelimiters = function (number, precision = 2, thousands = ',', decimal = '.') {
          if (!Number.isFinite(number)) return '0';

          const parts = number.toFixed(precision).split('.');
          const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          return whole + (parts[1] ? decimal + parts[1] : '');
      };

      const match = formatString.match(placeholderRegex);
      let value;
      switch (match ? match[1] : 'amount') {
          case 'amount_no_decimals':
              value = formatWithDelimiters(amount, 0);
              break;
          case 'amount_with_comma_separator':
              value = formatWithDelimiters(amount, 2, '.', ',');
              break;
          case 'amount_no_decimals_with_comma_separator':
              value = formatWithDelimiters(amount, 0, '.', ',');
              break;
          case 'amount_with_apostrophe_separator':
              value = formatWithDelimiters(amount, 2, "'", '.');
              break;
          default:
              value = formatWithDelimiters(amount, 2);
      }

      return formatString.replace(placeholderRegex, value);
  };

  const readRateCache = function () {
      try {
          return JSON.parse(window.sessionStorage.getItem(cartRateCacheKey) || '{}');
      } catch (error) {
          return {};
      }
  };

  const writeRateCache = function (cache) {
      try {
          window.sessionStorage.setItem(cartRateCacheKey, JSON.stringify(cache));
      } catch (error) {
      }
  };

  const getBaseCurrency = function () {
      const cartThresholdElement = document.querySelector('[data-base-currency]');
      return cartThresholdElement?.dataset.baseCurrency || window.shopCurrency || Shopify?.currency?.shopCurrency || Shopify?.currency?.currentCurrency || Shopify?.currency?.active || '';
  };

  const getActiveCurrency = function () {
      return Shopify?.currency?.active || document.querySelector('.currency-code')?.textContent?.trim() || getBaseCurrency();
  };

  const getCachedRate = function (baseCurrency, activeCurrency) {
      const cache = readRateCache();
      return Number(cache[`${baseCurrency}_${activeCurrency}`]);
  };

  const setCachedRate = function (baseCurrency, activeCurrency, rate) {
      if (!Number.isFinite(rate) || rate <= 0) {
          return;
      }

      const cache = readRateCache();
      cache[`${baseCurrency}_${activeCurrency}`] = rate;
      writeRateCache(cache);
  };

  const getDirectCurrencyRate = function (baseCurrency, activeCurrency) {
      if (!baseCurrency || !activeCurrency || baseCurrency === activeCurrency) {
          return 1;
      }

      const candidateRateSources = [
          () => Number(Shopify?.currency?.rate),
          () => {
              const rates = Shopify?.currency?.rates || Shopify?.Currency?.rates || window.Currency?.rates;
              if (!rates) {
                  return null;
              }

              const activeRate = Number(rates[activeCurrency]);
              const baseRate = Number(rates[baseCurrency] || 1);

              if (!Number.isFinite(activeRate) || activeRate <= 0 || !Number.isFinite(baseRate) || baseRate <= 0) {
                  return null;
              }

              return activeRate / baseRate;
          }
      ];

      for (const getRate of candidateRateSources) {
          const rate = getRate();
          if (Number.isFinite(rate) && rate > 0) {
              setCachedRate(baseCurrency, activeCurrency, rate);
              return rate;
          }
      }

      const cachedRate = getCachedRate(baseCurrency, activeCurrency);
      if (Number.isFinite(cachedRate) && cachedRate > 0) {
          return cachedRate;
      }

      return null;
  };

  const inferCurrencyRateFromCart = async function ({
      activeCartTotal,
      activeCurrency = getActiveCurrency(),
      baseCurrency = getBaseCurrency()
  } = {}) {
      if (!baseCurrency || !activeCurrency || baseCurrency === activeCurrency) {
          return 1;
      }

      if (!Number.isFinite(activeCartTotal) || activeCartTotal <= 0) {
          return getCachedRate(baseCurrency, activeCurrency);
      }

      const cartUrl = `${window.Shopify.routes.root}cart.js?currency=${baseCurrency}`;

      try {
          const response = await window.fetch(cartUrl, {
              credentials: 'same-origin',
              method: 'GET',
          });
          const baseCart = await response.json();
          const baseCartTotal = Number(baseCart?.total_price);

          if (!Number.isFinite(baseCartTotal) || baseCartTotal <= 0) {
              return getCachedRate(baseCurrency, activeCurrency);
          }

          const rate = activeCartTotal / baseCartTotal;

          if (Number.isFinite(rate) && rate > 0) {
              setCachedRate(baseCurrency, activeCurrency, rate);
              return rate;
          }
      } catch (error) {
      }

      return getCachedRate(baseCurrency, activeCurrency);
  };

  const resolvePresentmentAmount = async function (baseAmount, options = {}) {
      const normalizedBaseAmount = Number(baseAmount);
      const baseCurrency = options.baseCurrency || getBaseCurrency();
      const activeCurrency = options.activeCurrency || getActiveCurrency();

      if (!Number.isFinite(normalizedBaseAmount) || normalizedBaseAmount <= 0) {
          return normalizedBaseAmount;
      }

      if (!baseCurrency || !activeCurrency || baseCurrency === activeCurrency) {
          return normalizedBaseAmount;
      }

      let rate = getDirectCurrencyRate(baseCurrency, activeCurrency);

      if ((!Number.isFinite(rate) || rate <= 0) && window.Currency?.convert) {
          const convertedAmount = Number(window.Currency.convert(normalizedBaseAmount, baseCurrency, activeCurrency));

          if (Number.isFinite(convertedAmount) && convertedAmount > 0) {
              rate = convertedAmount / normalizedBaseAmount;
              setCachedRate(baseCurrency, activeCurrency, rate);
          }
      }

      if (!Number.isFinite(rate) || rate <= 0) {
          rate = await inferCurrencyRateFromCart({
              activeCartTotal: Number(options.activeCartTotal),
              activeCurrency,
              baseCurrency
          });
      }

      if (!Number.isFinite(rate) || rate <= 0) {
          if (activeCurrency === 'BAM') {
              return normalizedBaseAmount * 2;
          }

          return normalizedBaseAmount;
      }

      return Number((normalizedBaseAmount * rate).toFixed(2));
  };

  // Expose formatMoney globally
  window.formatMoney = formatMoney;
  window.currencyUtils = {
      getActiveCurrency,
      getBaseCurrency,
      getDirectCurrencyRate,
      inferCurrencyRateFromCart,
      resolvePresentmentAmount
  };
})();
